import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';

import { Constants } from '../common/Constants';
import { SessionCookie, emptyCookie } from '../models/SessionCookie';

export const SESSION_COOKIE: string = 'FH_MU';

export const parseSessionCookie = (cookie: string): SessionCookie => {
  const cookieData = cookie?.split('!');

  if (cookieData?.length === 3) {
    return {
      sessionId: cookieData[0],
      lastAccessedTime: parseInt(cookieData[1], 10),
      sessionDuration: parseInt(cookieData[2], 10),
    };
  }

  return emptyCookie;
};

export const isSessionValid = (cookie: SessionCookie): boolean => {
  const { lastAccessedTime, sessionDuration } = cookie;

  if (lastAccessedTime && sessionDuration) {
    const accessTimeMillis = lastAccessedTime * 1000;
    const durationMillis = sessionDuration * 1000;

    if (new Date().getTime() < accessTimeMillis + durationMillis) {
      return true;
    }
  }
  return false;
};

export const loginNotRequired = (): boolean => {
  const hostname = window?.location?.hostname?.toLowerCase();
  return (
    hostname === 'localhost' ||
    hostname?.startsWith('findcare-') ||
    hostname?.includes('.guest.uhc.')
  );
};

export const stageEnv = (): boolean => {
  const hostname = window?.location?.hostname?.toLowerCase();
  return hostname?.startsWith('findcare-stage');
};

export const handleSignout = () => {
  sessionStorage.removeItem('hasDismissedPopover');
  sessionStorage.removeItem('hasDismissedCoachMarkWeb');
  sessionStorage.removeItem('smartChoicePopoverDismissed');
  sessionStorage.removeItem(
    Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB
  );
  sessionStorage.removeItem(
    Constants.STORAGE_KEYS.SESSION.HIGH_PRIORITY_POPOVER
  );
};

export const logout = (setSessionId) => {
  setSessionId('');
  handleSignout();
  // @ts-expect-error This is a eslint error suggested by --fix command
  window.location.assign(config('UHG_LOGOUT_URL'));
};

export const validateSession = (sessionId, setSessionId): boolean => {
  const sessionCookie = storage.cookie.get(SESSION_COOKIE);

  if (sessionCookie) {
    const cookie = parseSessionCookie(sessionCookie);

    if (isSessionValid(cookie)) {
      if (!sessionId) {
        setSessionId(cookie.sessionId);
      }
      return true;
    } else {
      logout(setSessionId);
      return false;
    }
  } else if (loginNotRequired()) {
    if (sessionId) {
      logout(setSessionId);
      return false;
    }
    return false;
  } else {
    logout(setSessionId);
    return false;
  }
};

//create guest session
export const callGuestSessionUrl = async (
  useGuestPersistence = false,
  overrideUrl?: string
) => {
  const baseUrl = config('GUEST_BASE_URL') ?? '';
  const defaultEndPoint = `${baseUrl}/api/create-guest-session`;
  const endPoint = overrideUrl || defaultEndPoint;
  const createGuestSessionUrl = useGuestPersistence
    ? `${endPoint}?useGuestPersistence=true`
    : endPoint;
  try {
    // When using a portal-provided URL, the endpoint is proxied on the portal's
    // own origin so credentials:include is needed for Set-Cookie to be applied.
    const fetchOptions: RequestInit = overrideUrl
      ? { credentials: 'include' }
      : {};
    await fetch(createGuestSessionUrl, fetchOptions);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize guest session:', error);
  }
};

export const callAuthorizeGuestSessionUrl = async () => {
  if (window?.location?.hostname === 'localhost') {
    return;
  }

  const baseUrl = config('GUEST_BASE_URL') ?? '';
  const defaultEndPoint = `${baseUrl}/api/authorize-guest-session`;
  try {
    await fetch(defaultEndPoint, {
      method: 'GET',
      credentials: 'include',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to authorize guest session:', error);
  }
};
